<template>
  <transition name="el-fade-in">
    <div
      v-if="visible"
      ref="noticeRef"
      class="cs-workbench-notice"
      :class="{ 'is-dragging': dragging }"
      :style="noticeStyle"
      @pointerdown="onDragStart"
      @pointermove="onDragMove"
      @pointerup="onDragEnd"
      @pointercancel="onDragEnd"
    >
      <span class="cs-workbench-notice__avatar">
        <span class="cs-workbench-notice__logo" :class="`is-${presence}`">
          <img :src="logoImg" alt="" draggable="false" />
        </span>
        <el-button
          v-if="unreadTotal > 0"
          type="danger"
          round
          size="small"
          class="cs-workbench-notice__badge"
          title="点击回到工作台查看未读会话"
          @click.stop="openUnread"
          >{{ unreadLabel }}</el-button
        >
      </span>

      <!-- 电话快捷区：只在离台且桥接可用时出现，按钮调用工作台既有处理函数 -->
      <template v-if="callVisible">
        <el-button
          text
          size="small"
          class="cs-workbench-notice__call"
          title="回到工作台查看通话"
          @click.stop="goBack"
        >
          <template v-if="call.phase === 'ringing'">来电 · {{ call.phone || '未知号码' }}</template>
          <template v-else-if="call.phase === 'calling'"
            >外呼中 · {{ call.phone || '未知号码' }}</template
          >
          <template v-else>通话中 {{ durationText }}</template>
        </el-button>
        <template v-if="call.phase === 'ringing'">
          <el-button
            type="success"
            circle
            class="cs-workbench-notice__action is-answer"
            title="接听"
            aria-label="接听"
            @click.stop="onAnswer"
          >
            <Icon icon="ri:phone-fill" :size="16" />
          </el-button>
          <el-button
            type="danger"
            circle
            class="cs-workbench-notice__action is-reject"
            title="拒接"
            aria-label="拒接"
            @click.stop="onReject"
          >
            <Icon icon="mdi:phone-hangup" :size="16" />
          </el-button>
        </template>
        <el-button
          v-else
          type="danger"
          circle
          class="cs-workbench-notice__action is-hangup"
          title="挂断"
          aria-label="挂断"
          @click.stop="onHangup"
        >
          <Icon icon="mdi:phone-hangup" :size="16" />
        </el-button>
      </template>

      <el-button
        type="primary"
        plain
        round
        size="small"
        class="cs-workbench-notice__back"
        @click="goBack"
        >客服工作台</el-button
      >
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { useCsAgentStore } from '@/store/modules/csAgent'
import { CS_ROUTE } from '@/views/CustomerService/constants'
import { workbenchBridge } from '@/views/CustomerService/workbench/workbench-bridge'
import logoImg from '@/assets/imgs/logo.png'

/**
 * 全局「工作中」浮层。
 *
 * 工作台改为 keep-alive 后，离开路由不会签出；这里给坐席一个始终可见的在线提示和返回入口，
 * 避免在知识库 / 工单页查资料时忘记自己仍在接单。
 * 浮层可拖动（按住除按钮外的区域），位置写入 localStorage，视口变化时自动收回可见范围。
 */
defineOptions({ name: 'WorkbenchNotice' })

const route = useRoute()
const router = useRouter()
const agentStore = useCsAgentStore()

const presence = computed(() => agentStore.presence)

const visible = computed(
  () =>
    agentStore.online &&
    route.path !== CS_ROUTE.workbench &&
    !route.path.startsWith('/login') &&
    route.path !== '/404'
)

const goBack = () => {
  void router.push(CS_ROUTE.workbench)
}

/* ==================== 悬浮条投影（只读 + 快捷动作） ==================== */

const bridgeState = workbenchBridge.state
const unreadTotal = computed(() => bridgeState.unreadTotal)
const unreadLabel = computed(() => (unreadTotal.value > 99 ? '99+' : String(unreadTotal.value)))
const call = computed(() => bridgeState.call)
const callVisible = computed(
  () => bridgeState.ready && bridgeState.phoneAllowed && call.value.phase !== 'idle'
)

/** 通话计时：只在 talking 阶段跑秒表，数值仅用于展示 */
const nowTs = ref(Date.now())
let durationTimer: ReturnType<typeof setInterval> | null = null
const stopDurationTimer = () => {
  if (durationTimer) clearInterval(durationTimer)
  durationTimer = null
}
watch(
  () => call.value.phase,
  (phase) => {
    stopDurationTimer()
    if (phase === 'talking') {
      nowTs.value = Date.now()
      durationTimer = setInterval(() => (nowTs.value = Date.now()), 1000)
    }
  },
  { immediate: true }
)

const durationText = computed(() => {
  const startedAt = call.value.startedAt
  if (!startedAt) return '00:00'
  const seconds = Math.max(0, Math.floor((nowTs.value - startedAt) / 1000))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
})

/** 接听：调用工作台既有 onPopupAccept；接听属后台动作，不打断当前页面 */
const onAnswer = async () => {
  await workbenchBridge.actions?.answer?.()
}

/** 拒接：调用工作台既有 onPopupReject（内部关闭弹屏并清通话态） */
const onReject = () => {
  void workbenchBridge.actions?.reject?.()
}

/** 挂断：调用工作台既有 endCurrentService('call') */
const onHangup = () => {
  void workbenchBridge.actions?.hangup?.()
}

/** 红点：回到工作台并打开第一个未读会话（由工作台 onActivated 消费） */
const openUnread = () => {
  const sessionId = bridgeState.unreadSessionIds[0]
  if (sessionId) workbenchBridge.requestOpenSession(sessionId)
  goBack()
}

/* ==================== 拖动 ==================== */

const STORAGE_KEY = 'cs:workbench-notice-pos'
/** 距视口边缘的最小留白 */
const EDGE_GAP = 8

interface NoticePos {
  x: number
  y: number
}

const readStoredPos = (): NoticePos | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '') as NoticePos
    return typeof parsed?.x === 'number' && typeof parsed?.y === 'number' ? parsed : null
  } catch {
    return null
  }
}

const noticeRef = ref<HTMLElement>()
/** null = 使用默认的右下角定位；非空 = 用户拖动后的 left/top */
const pos = ref<NoticePos | null>(readStoredPos())
const dragging = ref(false)

let dragState: {
  startX: number
  startY: number
  originX: number
  originY: number
  moved: boolean
} | null = null

const clampPos = (x: number, y: number, width: number, height: number): NoticePos => ({
  x: Math.min(Math.max(EDGE_GAP, x), Math.max(EDGE_GAP, window.innerWidth - width - EDGE_GAP)),
  y: Math.min(Math.max(EDGE_GAP, y), Math.max(EDGE_GAP, window.innerHeight - height - EDGE_GAP))
})

const noticeStyle = computed(() =>
  pos.value
    ? { left: `${pos.value.x}px`, top: `${pos.value.y}px`, right: 'auto', bottom: 'auto' }
    : {}
)

/** 把已保存的位置收回到当前视口内（窗口缩放 / 旋转后） */
const keepInView = () => {
  if (!pos.value) return
  const element = noticeRef.value
  if (!element) return
  const rect = element.getBoundingClientRect()
  pos.value = clampPos(pos.value.x, pos.value.y, rect.width, rect.height)
}

/** 窗口缩放：布局回流后宽度可能变化，下一帧再校正一次 */
const onWindowResize = () => {
  keepInView()
  window.requestAnimationFrame(keepInView)
}

const onDragStart = (event: PointerEvent) => {
  // 「返回工作台」按钮保持原有点击行为，不参与拖动
  if ((event.target as HTMLElement | null)?.closest('button')) return
  const element = noticeRef.value
  if (!element) return
  const rect = element.getBoundingClientRect()
  // 从 right/bottom 切换到 left/top，保持位置不跳
  pos.value = { x: rect.left, y: rect.top }
  dragState = {
    startX: event.clientX,
    startY: event.clientY,
    originX: rect.left,
    originY: rect.top,
    moved: false
  }
  dragging.value = true
  element.setPointerCapture?.(event.pointerId)
  event.preventDefault()
}

const onDragMove = (event: PointerEvent) => {
  if (!dragging.value || !dragState) return
  const dx = event.clientX - dragState.startX
  const dy = event.clientY - dragState.startY
  if (!dragState.moved && Math.abs(dx) + Math.abs(dy) > 3) dragState.moved = true
  const element = noticeRef.value
  if (!element) return
  pos.value = clampPos(
    dragState.originX + dx,
    dragState.originY + dy,
    element.offsetWidth,
    element.offsetHeight
  )
}

const onDragEnd = (event: PointerEvent) => {
  if (!dragging.value) return
  dragging.value = false
  noticeRef.value?.releasePointerCapture?.(event.pointerId)
  if (dragState?.moved && pos.value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value))
    } catch {
      // 隐私模式忽略
    }
  }
  dragState = null
}

let noticeResizeObserver: ResizeObserver | null = null

const disconnectResizeObserver = () => {
  noticeResizeObserver?.disconnect()
  noticeResizeObserver = null
}

/** 浮层可见后才挂 ResizeObserver（元素是 v-if，组件挂载时可能还不存在） */
const observeResize = () => {
  disconnectResizeObserver()
  if (typeof ResizeObserver === 'undefined' || !noticeRef.value) return
  noticeResizeObserver = new ResizeObserver(() => keepInView())
  noticeResizeObserver.observe(noticeRef.value)
}

watch(
  visible,
  (value) => {
    if (value) {
      nextTick(() => {
        keepInView()
        observeResize()
      })
    } else {
      disconnectResizeObserver()
    }
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('resize', onWindowResize)
})

onBeforeUnmount(() => {
  stopDurationTimer()
  window.removeEventListener('resize', onWindowResize)
  disconnectResizeObserver()
})
</script>

<style lang="scss" scoped>
.cs-workbench-notice {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 3000;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100vw - 16px);
  padding: 4px 12px;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &.is-dragging {
    cursor: grabbing;
  }
}

.cs-workbench-notice__avatar {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  margin: 0 2px 0 -10px;
}

.cs-workbench-notice__logo {
  display: flex;
  flex: none;
  width: 38px;
  height: 38px;
  margin: 0;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    -webkit-user-drag: none;
  }
}

.cs-workbench-notice__badge {
  position: absolute;
  top: -4px;
  right: -6px;
}

.cs-workbench-notice__call {
  flex: none;
  max-width: 220px;
  overflow: hidden;
}

.cs-workbench-notice__action {
  flex: none;
}

.cs-workbench-notice__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cs-workbench-notice__back {
  flex: none;
  white-space: nowrap;
}
</style>

<!-- 工作台自定义外观（集中维护）：全局只加载一次，负责 .cs-workbench / .cs-workbench-notice 的 EP 覆盖 -->
<style lang="scss">
// stylelint-disable-next-line at-rule-no-unknown -- Sass 模块聚合入口
@use '../../views/CustomerService/workbench/styles/custom.scss';
</style>
